import { formatBirthdayForDisplay } from '#modules/localization';

import type { AssessmentDateValidation } from './validateAssessmentDate';
import type { AssessmentNumberValidation } from './validateAssessmentNumber';

/**
 * `t` from `useT()`, narrowed to the two call shapes this layer uses. Taken as
 * a parameter rather than read off the hook so every validation helper here
 * and in `evaluateQuestionAnswer` stays a pure function — callable from a
 * component, from another validator, and from a unit test without rendering
 * anything.
 */
export type Translate = (str: string, params?: Record<string, unknown>) => string;

// Picks error copy for a failed date validation. Shared between the
// standalone date question and any repeating-list column authored with
// `cellType: "date"`, so both surfaces read bounds off the same validation
// result instead of re-deriving them.
export function dateErrorMessage(
  { min, max, reason }: AssessmentDateValidation,
  t: Translate
): string {
  return reason === 'unparseable'
    ? t('Enter a valid date (MM/DD/YYYY).')
    : reason === 'after-max' && max
      ? t('Enter a date on or before {date}.', { date: formatBirthdayForDisplay(max) })
      : t('Enter a date on or after {date}.', { date: formatBirthdayForDisplay(min) });
}

// Picks error copy for a failed numeric validation. Shared between the
// standalone numeric question and any repeating-list column authored with
// `cellType: "number"` — see `dateErrorMessage` above for why this is split
// out rather than inlined at each call site.
export function numberErrorMessage(
  { min, max, reason }: AssessmentNumberValidation,
  t: Translate
): string {
  return reason === 'not-a-number'
    ? t('Enter a number.')
    : reason === 'after-max' && max !== undefined
      ? t('Enter a number of {max} or less.', { max })
      : t('Enter a number of {min} or more.', { min });
}
